import React, { useState, useEffect, useCallback } from 'react';
import { TrainerReportsTab } from './ProgressReportCard';

// Individual Client Profile - Everfit Style
const EverfitClientProfile = ({ client, onClose, backendUrl }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [clientData, setClientData] = useState({
    goals: [],
    tasks: [],
    nutritionPlan: null,
    mealPlans: [],
    progressPhotos: [],
    performanceMetrics: [],
    assessments: [],
    habits: [],
    workoutSessions: [],
    macroLogs: [],
    weightLogs: [],
    bodyMeasurements: []
  });
  const [loading, setLoading] = useState(true);

  const fetchAllClientData = useCallback(async () => {
    if (!client) return;
    
    try {
      setLoading(true);
      
      // Fetch all client data in parallel
      const [
        goalsRes,
        tasksRes,
        nutritionRes,
        mealPlansRes,
        photosRes,
        metricsRes,
        assessmentsRes,
        habitsRes,
        workoutSessionsRes,
        macroLogsRes,
        weightLogsRes,
        bodyMeasurementsRes
      ] = await Promise.all([
        fetch(`${backendUrl}/api/clients/${client.id}/goals`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/tasks`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/nutrition-plan`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/meal-plans`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/progress-photos`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/performance-metrics`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/assessments`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/habits`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/workout-sessions`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/macro-logs`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/weight-logs`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/body-measurements`, { credentials: 'include' })
      ]);

      const data = {
        goals: goalsRes.ok ? (await goalsRes.json()).goals : [],
        tasks: tasksRes.ok ? (await tasksRes.json()).tasks : [],
        nutritionPlan: nutritionRes.ok ? (await nutritionRes.json()).nutrition_plan : null,
        mealPlans: mealPlansRes.ok ? (await mealPlansRes.json()).meal_plans : [],
        progressPhotos: photosRes.ok ? (await photosRes.json()).progress_photos : [],
        performanceMetrics: metricsRes.ok ? (await metricsRes.json()).performance_metrics : [],
        assessments: assessmentsRes.ok ? (await assessmentsRes.json()).assessments : [],
        habits: habitsRes.ok ? (await habitsRes.json()).habits : [],
        workoutSessions: workoutSessionsRes.ok ? (await workoutSessionsRes.json()).sessions.filter(s => s.client_id === client.id) : [],
        macroLogs: macroLogsRes.ok ? (await macroLogsRes.json()).macro_logs : [],
        weightLogs: weightLogsRes.ok ? (await weightLogsRes.json()).weight_logs : [],
        bodyMeasurements: bodyMeasurementsRes.ok ? (await bodyMeasurementsRes.json()).body_measurements : []
      };

      setClientData(data);
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  }, [client, backendUrl]);

  useEffect(() => {
    if (client) {
      fetchAllClientData();
    }
  }, [client, fetchAllClientData]);

  if (!client) return null;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'training', label: 'Training' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'metrics', label: 'Metrics' },
    { id: 'reports', label: 'Reports' },
    { id: 'food_journal', label: 'Food Journal' },
    { id: 'macros', label: 'Macros' },
    { id: 'meal_plan', label: 'Meal Plan' },
    { id: 'documents', label: 'Documents' },
    { id: 'settings', label: 'Settings' }
  ];

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <button 
              onClick={onClose}
              className="hover:text-gray-700"
            >
              CLIENTS
            </button>
            <span>/</span>
            <span>All Clients</span>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Client Header */}
        <div className="flex items-center space-x-4 mt-4">
          <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-xl">
              {client.name?.charAt(0)?.toUpperCase() || 'C'}
            </span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
            <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
              <span>{client.email}</span>
              <span>•</span>
              <span>{client.phone || '2094293651'}</span>
              <span>•</span>
              <span>7:49 PM - America/Chicago</span>
              <span>•</span>
              <span>Program: {clientData.assessments?.[0]?.exercise_experience || 'Rotation 1'}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-6 mt-6 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading client data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <OverviewTab 
                client={client} 
                clientData={clientData}
                backendUrl={backendUrl}
                onRefresh={fetchAllClientData}
              />
            )}

            {/* Training Tab */}
            {activeTab === 'training' && (
              <TrainingTab 
                client={client} 
                workoutSessions={clientData.workoutSessions}
                backendUrl={backendUrl}
              />
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <TasksTab 
                client={client} 
                tasks={clientData.tasks}
                backendUrl={backendUrl}
                onRefresh={fetchAllClientData}
              />
            )}

            {/* Metrics Tab */}
            {activeTab === 'metrics' && (
              <MetricsTab 
                client={client} 
                performanceMetrics={clientData.performanceMetrics}
                weightLogs={clientData.weightLogs}
                bodyMeasurements={clientData.bodyMeasurements}
                backendUrl={backendUrl}
              />
            )}

            {/* Food Journal Tab */}
            {activeTab === 'reports' && <TrainerReportsTab clientId={client.id} />}

            {activeTab === 'food_journal' && (
              <FoodJournalTab 
                client={client} 
                macroLogs={clientData.macroLogs}
                backendUrl={backendUrl}
              />
            )}

            {/* Macros Tab */}
            {activeTab === 'macros' && (
              <MacrosTab 
                client={client} 
                macroLogs={clientData.macroLogs}
                nutritionPlan={clientData.nutritionPlan}
                backendUrl={backendUrl}
                onRefresh={fetchAllClientData}
              />
            )}

            {/* Meal Plan Tab */}
            {activeTab === 'meal_plan' && (
              <MealPlanTab 
                client={client} 
                mealPlans={clientData.mealPlans}
                nutritionPlan={clientData.nutritionPlan}
                backendUrl={backendUrl}
              />
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <DocumentsTab 
                client={client} 
                backendUrl={backendUrl}
              />
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <SettingsTab 
                client={client} 
                backendUrl={backendUrl}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ client, clientData, backendUrl, onRefresh }) => {
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showCountdownModal, setShowCountdownModal] = useState(false);

  // Calculate training stats
  const last7DaysWorkouts = clientData.workoutSessions.filter(session => {
    const sessionDate = new Date(session.scheduled_date);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return sessionDate >= sevenDaysAgo && session.status === 'completed';
  }).length;

  const last30DaysWorkouts = clientData.workoutSessions.filter(session => {
    const sessionDate = new Date(session.scheduled_date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return sessionDate >= thirtyDaysAgo && session.status === 'completed';
  }).length;

  const nextWeekAssigned = clientData.workoutSessions.filter(session => {
    const sessionDate = new Date(session.scheduled_date);
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return sessionDate >= today && sessionDate <= nextWeek && session.status === 'scheduled';
  }).length;

  // Get latest weight
  const latestWeight = clientData.weightLogs[0];

  return (
    <div className="space-y-6">
      {/* Training Stats */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Training</h3>
        <div className="grid grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {last7DaysWorkouts}/{Math.max(6, last7DaysWorkouts)}
            </div>
            <div className="text-sm text-gray-500 mt-1">LAST 7 DAYS</div>
            <div className="text-xs text-teal-600 mt-1">Tracked</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {last30DaysWorkouts}/{Math.max(6, last30DaysWorkouts * 4)}
            </div>
            <div className="text-sm text-gray-500 mt-1">LAST 30 DAYS</div>
            <div className="text-xs text-teal-600 mt-1">Tracked</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{nextWeekAssigned}</div>
            <div className="text-sm text-gray-500 mt-1">NEXT WEEK</div>
            <div className="text-xs text-teal-600 mt-1">Assigned</div>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <span className="font-medium">Last Workout:</span> {
            clientData.workoutSessions.length > 0 
              ? 'No workouts logged yet'
              : new Date(clientData.workoutSessions[0]?.completed_at || clientData.workoutSessions[0]?.scheduled_date).toLocaleDateString()
          }
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Goals & Countdown */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🎯</span>
              Goal & Countdown
            </h3>
            <span className="text-sm text-gray-500">(Shared with client)</span>
          </div>
          
          {clientData.goals.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">No goals set</div>
              <button
                onClick={() => setShowGoalModal(true)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                + Goal
              </button>
              <button
                onClick={() => setShowCountdownModal(true)}
                className="ml-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                + Countdown
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {clientData.goals.slice(0, 2).map((goal) => (
                <div key={goal.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium text-gray-900">{goal.title}</div>
                  <div className="text-sm text-gray-600 mt-1">{goal.description}</div>
                  {goal.target_date && (
                    <div className="text-xs text-gray-500 mt-2">
                      Due: {new Date(goal.target_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-sm text-gray-900">{client.email}</span>
            </div>
            <div className="flex items-center space-x-3">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-sm text-gray-900">{client.phone || '2094293651'}</span>
            </div>
            <div className="flex items-center space-x-3">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-gray-900">7:49 PM - America/Chicago</span>
            </div>
            <div className="flex items-center space-x-3">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 011-1h1a1 1 0 011 1v1m-4 0h4m-4 4v1m0-1h4m-4 1v1h4m-2-3h.01" />
              </svg>
              <span className="text-sm text-gray-900">Program: Rotation 1</span>
            </div>
          </div>
        </div>

        {/* Updates */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Updates</h3>
          <div className="space-y-3">
            {/* Recent updates */}
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-medium text-sm">
                  {client.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">{client.name}</span> uploaded body metrics
                </p>
                <p className="text-xs text-gray-500">5d</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-medium text-sm">JW</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">Jacob Wright</span> completed the form: Welcome Form - Copy
                </p>
                <p className="text-xs text-gray-500">1y</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-medium text-sm">JW</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">Jacob Wright</span> just joined as your client
                </p>
                <p className="text-xs text-gray-500">1y</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body Metrics */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Body Metrics Overview</h3>
          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            Update All
          </button>
        </div>

        <div className="grid grid-cols-2 gap-8">
          {/* Weight */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Weight</span>
              <span className="text-sm text-gray-500">Last 4 weeks</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {latestWeight ? `${latestWeight.weight} lb` : '160 lb'}
              <span className="text-sm font-normal text-red-600 ml-2">↑ 1.8%</span>
            </div>
            <div className="text-sm text-gray-500">140.5</div>
            
            {/* Mini chart placeholder */}
            <div className="mt-4 h-20 bg-gray-50 rounded flex items-end justify-between px-2 py-2">
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <div key={day} className="flex flex-col items-center">
                  <div 
                    className="w-2 bg-blue-500 rounded-t"
                    style={{ height: `${Math.random() * 60 + 20}px` }}
                  ></div>
                  <span className="text-xs text-gray-400 mt-1">{25 + day}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Body Fat */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Body Fat</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">— %</div>
            
            {/* Empty state */}
            <div className="mt-4 h-20 bg-gray-50 rounded flex items-center justify-center">
              <div className="text-center">
                <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                <p className="text-xs text-gray-500">No data available</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes, Limitations, Progress Photo */}
      <div className="grid grid-cols-3 gap-6">
        {/* Notes */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">📝</span>
              Notes
            </h3>
            <span className="text-sm text-gray-500">2</span>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <input
              type="text"
              placeholder="Add a note about {client.name}..."
              className="w-full bg-transparent border-none focus:outline-none text-sm text-gray-700 placeholder-gray-500"
            />
          </div>
        </div>

        {/* Limitations/Injuries */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🚨</span>
              Limitations/Injuries
            </h3>
            <span className="text-sm text-gray-500">2</span>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <input
              type="text"
              placeholder="Add any medical note or injury about {client.name}..."
              className="w-full bg-transparent border-none focus:outline-none text-sm text-gray-700 placeholder-gray-500"
            />
          </div>
        </div>

        {/* Progress Photo */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">📷</span>
              Progress Photo
            </h3>
          </div>
          <div className="text-center py-8">
            <div className="text-gray-400 mb-2">{client.name} hasn't uploaded any photos.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Macros Tab Component (matches the Everfit screenshot exactly)
const MacrosTab = ({ client, macroLogs, nutritionPlan, backendUrl, onRefresh }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('Weekly');
  const [selectedDateRange, setSelectedDateRange] = useState('Aug 11 - Aug 17');
  const [showGoalsModal, setShowGoalsModal] = useState(false);

  // Calculate macro data for chart
  const weekData = [
    { day: 'MON 11', date: '11' },
    { day: 'TUE 12', date: '12' },
    { day: 'WED 13', date: '13' },
    { day: 'THU 14', date: '14' },
    { day: 'FRI 15', date: '15' },
    { day: 'SAT 16', date: '16' },
    { day: 'SUN 17', date: '17' }
  ];

  const currentMacros = {
    protein: { daily: 0, goal: 0 },
    carbs: { daily: 0, goal: 0 },
    fat: { daily: 0, goal: 0 },
    calories: { daily: 0, goal: 0 }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Macros Report</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">View by</label>
            <select 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option>Weekly</option>
              <option>Monthly</option>
              <option>Daily</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">📅</span>
            <select 
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option>Aug 11 - Aug 17</option>
            </select>
          </div>
        </div>
      </div>

      {/* Daily Calories Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Daily Calories (Kcal)</h3>
          <select className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
            <option>All</option>
          </select>
        </div>

        {/* Legend */}
        <div className="flex items-center space-x-6 mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span className="text-sm text-gray-600">Protein</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-600">Carbs</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span className="text-sm text-gray-600">Fat</span>
          </div>
        </div>

        {/* Chart */}
        <div className="h-64 bg-gray-50 rounded-lg flex items-end justify-center relative">
          {/* Y-axis labels */}
          <div className="absolute left-4 top-0 h-full flex flex-col justify-between text-xs text-gray-400 py-4">
            <span>0</span>
            <span>-0.1</span>
            <span>-0.2</span>
            <span>-0.3</span>
            <span>-0.4</span>
            <span>-0.5</span>
            <span>-0.6</span>
            <span>-0.7</span>
            <span>-0.8</span>
          </div>

          {/* Chart bars */}
          <div className="flex items-end space-x-8 mb-8">
            {weekData.map((day) => (
              <div key={day.date} className="flex flex-col items-center">
                <div className="w-12 h-32 bg-white border border-gray-200 rounded-t relative flex items-end justify-center">
                  <span className="text-xs text-gray-400 mb-2">No data</span>
                </div>
                <span className="text-xs text-gray-500 mt-2">{day.day}</span>
              </div>
            ))}
          </div>

          {/* X-axis */}
          <div className="absolute bottom-4 right-4 flex items-end space-x-12">
            {weekData.map((day) => (
              <span key={day.date} className="text-xs text-gray-500">{day.date}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Calories Goal and Macro Distribution */}
      <div className="grid grid-cols-2 gap-6">
        {/* Calories Goal */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Calories Goal</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">✏️ Edit</span>
              <button
                onClick={() => setShowGoalsModal(true)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                📱
              </button>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 text-center">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-500 uppercase">DAILY AVG</div>
              <div className="text-sm font-medium text-gray-500 uppercase">GOAL</div>
            </div>
            <div className="space-y-2">
              <div className="font-semibold text-gray-900">—</div>
              <div className="font-semibold text-gray-900">—</div>
              <div className="text-xs text-gray-500">Protein</div>
            </div>
            <div className="space-y-2">
              <div className="font-semibold text-gray-900">—</div>
              <div className="font-semibold text-gray-900">—</div>
              <div className="text-xs text-gray-500">Carbs</div>
            </div>
            <div className="space-y-2">
              <div className="font-semibold text-gray-900">—</div>
              <div className="font-semibold text-gray-900">—</div>
              <div className="text-xs text-gray-500">Fat</div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <div className="text-lg font-semibold text-gray-900">—</div>
            <div className="text-sm text-gray-500">Calories</div>
          </div>
        </div>

        {/* Macro Distribution */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Macro Distribution</h3>
          
          {/* Donut chart placeholder */}
          <div className="flex items-center justify-center h-48">
            <div className="relative">
              <div className="w-32 h-32 rounded-full border-8 border-gray-200 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-400">0%</div>
                  <div className="text-xs text-gray-400">Avg</div>
                </div>
              </div>
            </div>
          </div>

          {/* Distribution percentages */}
          <div className="grid grid-cols-3 gap-4 mt-6 text-center">
            <div>
              <div className="text-sm font-medium text-gray-900">0%</div>
              <div className="text-xs text-gray-500">Protein</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">0%</div>
              <div className="text-xs text-gray-500">Carbs</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">0%</div>
              <div className="text-xs text-gray-500">Fat</div>
            </div>
          </div>
        </div>
      </div>

      {/* Totals by day */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Totals by day</h3>
          <div className="flex items-center space-x-4">
            <button className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              Percent (%)
            </button>
            <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg">
              Gram (g)
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left py-2 text-sm font-medium text-gray-500">DATE</th>
                <th className="text-left py-2 text-sm font-medium text-gray-500">PROTEIN</th>
                <th className="text-left py-2 text-sm font-medium text-gray-500">CARBS</th>
                <th className="text-left py-2 text-sm font-medium text-gray-500">FAT</th>
                <th className="text-left py-2 text-sm font-medium text-gray-500">CAL</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {weekData.map((day, index) => (
                <tr key={day.date} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="py-3 text-sm text-gray-900">{day.day}</td>
                  <td className="py-3 text-sm text-gray-900">—</td>
                  <td className="py-3 text-sm text-gray-900">—</td>
                  <td className="py-3 text-sm text-gray-900">—</td>
                  <td className="py-3 text-sm text-gray-900">—</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for other tabs
// Training Tab Component (exactly matching Everfit screenshot)
const TrainingTab = ({ client, workoutSessions, backendUrl }) => {
  const [selectedView, setSelectedView] = useState('results');
  const [workoutResults, setWorkoutResults] = useState([]);
  const [exerciseProgress, setExerciseProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [totalCompleted, setTotalCompleted] = useState(0);

  useEffect(() => {
    fetchWorkoutResults();
  }, [client]);

  const fetchWorkoutResults = async () => {
    if (!client) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/trainer/client/${client.id}/workout-results`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setWorkoutResults(data.workout_results || []);
        setExerciseProgress(data.exercise_progress || {});
        setTotalCompleted(data.total_completed || 0);
      }
    } catch (error) {
      console.error('Error fetching workout results:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-green-100 text-sm">Total Completed</p>
          <p className="text-3xl font-bold">{totalCompleted}</p>
          <p className="text-green-100 text-xs mt-1">workouts</p>
        </div>
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-blue-100 text-sm">Exercises Tracked</p>
          <p className="text-3xl font-bold">{Object.keys(exerciseProgress).length}</p>
          <p className="text-blue-100 text-xs mt-1">unique exercises</p>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <p className="text-purple-100 text-sm">Progress Data</p>
          <p className="text-3xl font-bold">
            {Object.values(exerciseProgress).reduce((sum, arr) => sum + arr.length, 0)}
          </p>
          <p className="text-purple-100 text-xs mt-1">data points</p>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center space-x-4">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setSelectedView('results')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedView === 'results'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            Workout Results
          </button>
          <button
            onClick={() => setSelectedView('progress')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedView === 'progress'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            Exercise Progress
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workout data...</p>
        </div>
      ) : (
        <>
          {/* Workout Results View */}
          {selectedView === 'results' && (
            <div className="space-y-4">
              {workoutResults.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Completed Workouts Yet</h3>
                  <p className="text-gray-600">Once {client.name} completes workouts, their results will appear here.</p>
                </div>
              ) : (
                workoutResults.map((workout, idx) => (
                  <div 
                    key={workout.id || idx} 
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Workout Header */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => setSelectedWorkout(selectedWorkout?.id === workout.id ? null : workout)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-3 h-3 rounded-full ${workout.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{workout.name || 'Workout'}</h4>
                            <p className="text-sm text-gray-500">
                              {formatDate(workout.completed_at || workout.scheduled_date)}
                              {workout.duration && ` • ${Math.round(workout.duration / 60)} min`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(workout.status)}`}>
                            {workout.status === 'completed' ? '✓ Completed' : 'In Progress'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {workout.exercises?.length || 0} exercises
                          </span>
                          <svg 
                            className={`w-5 h-5 text-gray-400 transition-transform ${selectedWorkout?.id === workout.id ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      
                      {/* Target muscles */}
                      {workout.target_muscles && workout.target_muscles.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {workout.target_muscles.map((muscle, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                              {muscle}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Expanded Exercise Details */}
                    {selectedWorkout?.id === workout.id && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4">
                        <h5 className="font-medium text-gray-900 mb-3">Exercise Details</h5>
                        <div className="space-y-3">
                          {workout.exercises?.map((exercise, exIdx) => (
                            <div key={exIdx} className="bg-white rounded-lg p-3 border border-gray-200">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
                                      {exIdx + 1}
                                    </span>
                                    <p className="font-medium text-gray-900">{exercise.name}</p>
                                  </div>
                                  
                                  {/* Prescribed vs Actual */}
                                  <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <p className="text-gray-500 text-xs mb-1">Prescribed</p>
                                      <p className="text-gray-700">
                                        {exercise.sets} sets × {exercise.reps} reps
                                        {exercise.weight && ` @ ${exercise.weight}`}
                                      </p>
                                    </div>
                                    
                                    {/* Actual completed sets */}
                                    {exercise.sets_completed && exercise.sets_completed.length > 0 && (
                                      <div>
                                        <p className="text-gray-500 text-xs mb-1">Actual</p>
                                        <div className="space-y-1">
                                          {exercise.sets_completed.map((set, setIdx) => (
                                            <p key={setIdx} className="text-green-700 font-medium">
                                              Set {setIdx + 1}: {set.weight || 0} lbs × {set.reps || 0} reps
                                            </p>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Exercise notes */}
                                  {exercise.notes && (
                                    <p className="text-xs text-gray-500 mt-2 italic">
                                      📝 {exercise.notes}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Workout Notes */}
                        {workout.workout_notes && (
                          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                            <p className="text-sm font-medium text-yellow-800">Client Notes:</p>
                            <p className="text-sm text-yellow-700 mt-1">{workout.workout_notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Exercise Progress View */}
          {selectedView === 'progress' && (
            <div className="space-y-4">
              {Object.keys(exerciseProgress).length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Progress Data Yet</h3>
                  <p className="text-gray-600">Progress tracking will appear once {client.name} logs weights for exercises.</p>
                </div>
              ) : (
                Object.entries(exerciseProgress).map(([exerciseName, history]) => (
                  <div key={exerciseName} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">{exerciseName}</h4>
                      <span className="text-sm text-gray-500">{history.length} entries</span>
                    </div>
                    
                    {/* Progress visualization */}
                    <div className="space-y-2">
                      {history.slice(0, 5).map((entry, idx) => {
                        const prevEntry = history[idx + 1];
                        const weightChange = prevEntry ? entry.weight - prevEntry.weight : 0;
                        
                        return (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm text-gray-500 w-24">{formatDate(entry.date)}</span>
                              <span className="font-medium text-gray-900">
                                {entry.weight} lbs × {entry.reps} reps
                              </span>
                            </div>
                            {weightChange !== 0 && (
                              <span className={`text-sm font-medium ${weightChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {weightChange > 0 ? '↑' : '↓'} {Math.abs(weightChange)} lbs
                              </span>
                            )}
                          </div>
                        );
                      })}
                      
                      {history.length > 5 && (
                        <p className="text-sm text-gray-500 text-center py-2">
                          +{history.length - 5} more entries
                        </p>
                      )}
                    </div>

                    {/* Best performance */}
                    {history.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Best Performance:</span>
                          <span className="font-bold text-green-600">
                            {Math.max(...history.map(h => h.weight || 0))} lbs
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const TasksTab = ({ client, tasks, backendUrl, onRefresh }) => (
  <div className="text-center py-12">
    <h3 className="text-lg font-semibold text-gray-900">Tasks & Assignments</h3>
    <p className="text-gray-600 mt-2">Task management for {client.name}</p>
  </div>
);

// Metrics Tab Component (exactly matching Everfit screenshot)
const MetricsTab = ({ client, performanceMetrics, weightLogs, bodyMeasurements, backendUrl }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('Last 4 weeks');
  const [activeMetricTab, setActiveMetricTab] = useState('body');
  const [showAddMetricModal, setShowAddMetricModal] = useState(false);

  // Sample weight data for chart (replace with real data)
  const weightData = [
    { date: 'Jul 21', weight: 158 },
    { date: 'Jul 28', weight: 159 },
    { date: 'Aug 2', weight: 161 },
    { date: 'Aug 7', weight: 160 },
    { date: 'Aug 12', weight: 160 },
    { date: 'Aug 17', weight: 160 }
  ];

  const currentWeight = weightLogs.length > 0 ? weightLogs[0].weight : 160;
  const previousWeight = 158;
  const weightChange = ((currentWeight - previousWeight) / previousWeight * 100).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveMetricTab('body')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeMetricTab === 'body'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📊 Body Metrics
          </button>
          <button
            onClick={() => setActiveMetricTab('exercise')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeMetricTab === 'exercise'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            🏋️ Exercise Metrics
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <select 
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option>Last 4 weeks</option>
            <option>Last 8 weeks</option>
            <option>Last 3 months</option>
            <option>Last 6 months</option>
          </select>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Update Results
          </button>
        </div>
      </div>

      {/* Body Metrics Tab */}
      {activeMetricTab === 'body' && (
        <>
          {/* All Metrics Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">All Metrics</h2>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">🔍</span>
              <button 
                onClick={() => setShowAddMetricModal(true)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
              >
                <span>+</span>
                <span>Add New</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6">
            {/* Left: Body Metrics Table */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Body Metrics</h3>
              
              {/* Metrics Filter */}
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-sm text-gray-500">📊</span>
                <span className="text-sm font-medium text-gray-700">All Metrics (3)</span>
              </div>

              {/* Metrics Table */}
              <div className="space-y-4">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="text-left py-2 text-xs font-medium text-gray-500 uppercase">Value</th>
                      <th className="text-left py-2 text-xs font-medium text-gray-500 uppercase">Last Update</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="py-3 text-sm font-medium text-gray-900">Weight</td>
                      <td className="py-3 text-sm text-gray-900">{currentWeight} lb</td>
                      <td className="py-3 text-sm text-gray-500">Aug 12</td>
                    </tr>
                    <tr>
                      <td className="py-3 text-sm font-medium text-gray-900">Body Fat</td>
                      <td className="py-3 text-sm text-gray-900">—</td>
                      <td className="py-3 text-sm text-gray-500">—</td>
                    </tr>
                    <tr>
                      <td className="py-3 text-sm font-medium text-gray-900">Steps</td>
                      <td className="py-3 text-sm text-gray-900">—</td>
                      <td className="py-3 text-sm text-gray-500">—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Middle: Weight Chart */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Weight</h3>
              
              {/* Current Weight Display */}
              <div className="flex items-baseline space-x-2 mb-1">
                <span className="text-3xl font-bold text-gray-900">{currentWeight}</span>
                <span className="text-lg text-gray-600">lb</span>
                <span className="text-sm text-red-600 flex items-center">
                  ↑ {weightChange}%
                </span>
              </div>
              <div className="text-sm text-gray-500 mb-6">{previousWeight}</div>

              {/* Weight Chart */}
              <div className="h-48 relative">
                <svg width="100%" height="100%" viewBox="0 0 300 180" className="border-b border-l border-gray-200">
                  {/* Grid lines */}
                  {[0, 1, 2, 3, 4].map((i) => (
                    <line
                      key={i}
                      x1="0"
                      y1={i * 45}
                      x2="300"
                      y2={i * 45}
                      stroke="#f3f4f6"
                      strokeWidth="1"
                    />
                  ))}
                  
                  {/* Weight line */}
                  <polyline
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    points={weightData.map((point, index) => 
                      `${(index * 60)},${180 - ((point.weight - 155) * 10)}`
                    ).join(' ')}
                  />
                  
                  {/* Weight points */}
                  {weightData.map((point, index) => (
                    <circle
                      key={index}
                      cx={index * 60}
                      cy={180 - ((point.weight - 155) * 10)}
                      r="4"
                      fill="#3b82f6"
                    />
                  ))}
                </svg>

                {/* X-axis labels */}
                <div className="flex justify-between mt-2 px-2">
                  {weightData.map((point, index) => (
                    <span key={index} className="text-xs text-gray-500">{point.date}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Body Fat & Steps */}
            <div className="space-y-6">
              {/* Body Fat */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Body Fat</h3>
                <div className="text-3xl font-bold text-gray-400 mb-6">— %</div>
                
                {/* Empty state chart */}
                <div className="h-24 bg-gray-50 rounded flex items-center justify-center">
                  <div className="text-center">
                    <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p className="text-xs text-gray-500">No data available</p>
                  </div>
                </div>
              </div>

              {/* Steps */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Steps</h3>
                <div className="text-3xl font-bold text-gray-400 mb-6">—</div>
                
                {/* Empty state chart */}
                <div className="h-24 bg-gray-50 rounded flex items-center justify-center">
                  <div className="text-center">
                    <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <p className="text-xs text-gray-500">No data available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Action */}
          <div className="text-center pt-8">
            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center mx-auto space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.168 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Manage Metrics</span>
            </button>
          </div>
        </>
      )}

      {/* Exercise Metrics Tab (placeholder) */}
      {activeMetricTab === 'exercise' && (
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-900">Exercise Metrics</h3>
          <p className="text-gray-600 mt-2">Exercise performance tracking coming soon</p>
        </div>
      )}
    </div>
  );
};

const FoodJournalTab = ({ client, macroLogs, backendUrl }) => (
  <div className="text-center py-12">
    <h3 className="text-lg font-semibold text-gray-900">Food Journal</h3>
    <p className="text-gray-600 mt-2">Daily food tracking for {client.name}</p>
  </div>
);

const MealPlanTab = ({ client, mealPlans, nutritionPlan, backendUrl }) => (
  <div className="text-center py-12">
    <h3 className="text-lg font-semibold text-gray-900">Meal Planning</h3>
    <p className="text-gray-600 mt-2">Meal plans and nutrition for {client.name}</p>
  </div>
);

const DocumentsTab = ({ client, backendUrl }) => (
  <div className="text-center py-12">
    <h3 className="text-lg font-semibold text-gray-900">Documents & Files</h3>
    <p className="text-gray-600 mt-2">Document management for {client.name}</p>
  </div>
);

const SettingsTab = ({ client, backendUrl }) => (
  <div className="text-center py-12">
    <h3 className="text-lg font-semibold text-gray-900">Client Settings</h3>
    <p className="text-gray-600 mt-2">Settings and preferences for {client.name}</p>
  </div>
);

export default EverfitClientProfile;